// ─── lessonData.jsx ───────────────────────────────────────────────────────────
// Lesson content for all training modules.
// Each module key maps to an array consumed by LessonPlayer.
//
// Lesson shape:
//   { id, title, type: "concept"|"calculator"|"quiz", accentColor?, content }
//
// Calculator lessons need: content.fields[], content.compute(vals), content.renderResults(results)
// Quiz lessons need:       content.questions[], content.passPct (0–1)
// ─────────────────────────────────────────────────────────────────────────────

// ── Shared helpers ────────────────────────────────────────────────────────────

function calcPI(loan, rate, years) {
  if (!loan || !rate || !years) return 0;
  const r = rate / 100 / 12, n = years * 12;
  if (r === 0) return loan / n;
  return (loan * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
}

function fmt$(n) {
  if (n >= 1000000) return `$${(n / 1000000).toFixed(2)}M`;
  if (n >= 1000)    return `$${(n / 1000).toFixed(1)}k`;
  return `$${Math.round(n)}`;
}

// ResultPanel renders inside calculator lessons.
// content.renderResults must return a plain object: { highlight, rows, tip }
// LessonPlayer renders it — no JSX needed here.
// Format: { highlight: {label,value,color,barPct?,barLabels?}, rows: [{label,value,color?}], tip }
// ─────────────────────────────────────────────────────────────────────────────


// ════════════════════════════════════════════════════════════════════════════
// LOAN PRODUCTS
// ════════════════════════════════════════════════════════════════════════════

// ─────────────────────────────────────────────────────────────
// BRIDGE / FIX & FLIP
// ─────────────────────────────────────────────────────────────
// ── tm_fix_flip: Fix & Flip Bridge Loans ─────────────────────────────────────
export const tm_bridge = [
  {
    id: "ff_intro",
    title: "What Is a Fix & Flip Loan?",
    type: "concept",
    accentColor: "#d97706",
    content: {
      headline: "Short-term capital to buy, renovate, and sell — the property is the collateral, the exit is the underwrite.",
      paragraphs: [
        "A fix & flip loan is a <strong>short-term bridge loan</strong> — typically 12 to 18 months — that funds both the purchase and the rehabilitation of an investment property. The borrower's plan to sell or refinance at a higher value after repairs is the core of the credit story.",
        "Unlike DSCR, there's no rent to measure. The lender is underwriting the <strong>deal</strong>: the purchase price, the scope of work, the after-repair value (ARV), and the investor's ability to execute.",
        "This is your highest-volume product for active real estate investors. Every house flipper, wholesale buyer, and BRRRR investor will need this at some point.",
      ],
      callout: { type: "tip", text: "Pitch it in one sentence: 'We fund the purchase and the renovation in one loan — you bring the deal, we bring the capital, you keep the spread.' That's the whole value proposition." },
    },
  },
  {
    id: "ff_arv",
    title: "ARV: The Most Important Number",
    type: "concept",
    accentColor: "#d97706",
    content: {
      headline: "ARV drives everything — the loan amount, the LTV, and whether the deal makes sense.",
      paragraphs: [
        "<strong>After-Repair Value (ARV)</strong> is what the property will be worth once renovations are complete. It's determined by comparable sales in the area — not what the borrower hopes it will sell for.",
        "The lender orders an <strong>ARV appraisal</strong> (also called a subject-to appraisal) that values the property as if the described scope of work has already been completed. This single number sets the ceiling for everything.",
      ],
      breakdown: [
        { letter: "A", word: "After", desc: "Valued as completed — not current as-is condition" },
        { letter: "R", word: "Repair", desc: "Full scope of renovations included in the appraisal" },
        { letter: "V", word: "Value", desc: "Market value supported by recent comparable sales (comps)" },
      ],
      formula: { label: "Max Loan Sizing", expression: "Loan = Lesser of: (ARV × LTV%) OR (Purchase + Rehab Budget)", sub: "Most programs cap at 90% LTC (loan-to-cost) as a second check" },
      callout: { type: "warning", text: "If your borrower's ARV is based on wishful thinking, the appraisal will kill the deal. Coach them to run comps before submitting — 'Your ARV needs to be defensible, not aspirational.'" },
    },
  },
  {
    id: "ff_structure",
    title: "Loan Structure & Draw Schedule",
    type: "concept",
    accentColor: "#d97706",
    content: {
      headline: "Two components, one loan: the purchase advance and the rehab holdback.",
      paragraphs: [
        "Fix & flip loans have a two-part structure most borrowers don't expect. The lender doesn't hand over the full loan at closing. The rehab budget is held in <strong>reserve (the holdback)</strong> and released in draws as work is completed and inspected.",
        "This protects both the lender and the borrower — funds are released in stages as value is actually created, not front-loaded into a project that may stall.",
      ],
      breakdown: [
        { letter: "1", word: "Initial Advance", desc: "Funded at closing — covers the purchase price and closing costs. Typically 80–90% of purchase price." },
        { letter: "2", word: "Rehab Holdback", desc: "The renovation budget held in reserve by the lender. Released in 2–4 draws during construction." },
        { letter: "3", word: "Draw Inspections", desc: "Before each draw, an inspector (or desktop review with photos) confirms work is complete. 3–5 business day turnaround." },
        { letter: "4", word: "Final Draw", desc: "Released at substantial completion. Typically 10–15% of rehab budget held back until final inspection passes." },
      ],
      callout: { type: "tip", text: "Draw timing is your biggest service differentiator. Investors live and die by cash flow. If you can promise 3-business-day draw turnarounds, say so — that's often more important than a quarter point on the rate." },
    },
  },
  {
    id: "ff_ltv",
    title: "LTV, LTC & Leverage Tiers",
    type: "concept",
    accentColor: "#d97706",
    content: {
      headline: "Two leverage metrics govern every fix & flip deal — know both cold.",
      paragraphs: [
        "Fix & flip loans use two parallel leverage checks. The lender approves the <strong>lesser of</strong> the ARV-based limit or the cost-based limit. Both caps apply simultaneously.",
      ],
      thresholds: [
        { range: "LTV: Up to 75%", label: "ARV-based cap", color: "#16a34a", bg: "#f0fdf4", border: "#bbf7d0", desc: "Maximum loan as % of the appraised After-Repair Value. Standard cap is 70–75% ARV. Best-in-class borrowers may access 75–80% ARV." },
        { range: "LTC: Up to 90%", label: "Cost-based cap", color: "#2563eb", bg: "#eff6ff", border: "#bfdbfe", desc: "Maximum loan as % of total project cost (purchase + rehab budget). Prevents over-leverage on distressed buys. Typical cap is 85–90% LTC." },
        { range: "Purchase: 80–90%", label: "Initial advance", color: "#ca8a04", bg: "#fffbeb", border: "#fde68a", desc: "Of purchase price funded at closing. Higher advance = less cash borrower needs at table. 90% purchase advance is a major selling point." },
        { range: "Rehab: 100%", label: "Full budget funded", color: "#f97316", bg: "#fff7ed", border: "#fed7aa", desc: "Rehab budget is funded in full via draws. Investor doesn't need liquid capital to fund renovations — just float between draws." },
      ],
      formula: { label: "Dual Test Example", expression: "ARV $400K × 75% = $300K max   |   Cost $280K × 90% = $252K max", sub: "Loan capped at $252K — cost test is the binding constraint here" },
      callout: { type: "warning", text: "Always run both tests before quoting a loan amount. Promising $300K based on ARV, then repricing to $252K at underwriting is a deal-killer relationship." },
    },
  },
  {
    id: "ff_qualifications",
    title: "Borrower Qualifications",
    type: "concept",
    accentColor: "#d97706",
    content: {
      headline: "Experience matters here more than income — lenders are underwriting the operator, not just the property.",
      paragraphs: [
        "Unlike DSCR, fix & flip underwriting weighs the borrower's track record heavily. A proven flipper with 10 completed projects gets better terms, higher leverage, and faster approvals than a first-timer with perfect credit.",
        "No income documentation is required — no W-2s, no tax returns, no paystubs. This is asset-based lending.",
      ],
      breakdown: [
        { letter: "✓", word: "Credit Score", desc: "Minimum 620–660 depending on program. 700+ unlocks best leverage and rates. No score = no deal." },
        { letter: "✓", word: "Experience Tier", desc: "0 flips, 1–4 flips, 5–9 flips, 10+ flips. Each tier unlocks higher LTV and lower rates. Always ask 'How many projects have you completed in the last 36 months?'" },
        { letter: "✓", word: "Liquidity / Reserves", desc: "Most lenders require 10–15% of total project cost in verified liquid reserves. This isn't the down payment — it's the proof they can float draws and handle surprises." },
        { letter: "✓", word: "Scope of Work (SOW)", desc: "Itemized renovation budget signed by contractor or investor. Must tie to the ARV appraisal. Vague 'TBD' line items kill approvals." },
        { letter: "✓", word: "Entity", desc: "Most fix & flip loans require closing in an LLC or corporation. Personal loans are rare. If borrower doesn't have an entity, they need to form one before closing." },
      ],
      callout: { type: "tip", text: "First-time flippers aren't disqualified — they just need a stronger deal. Compensating factors: higher down payment (lower LTV), stronger ARV comps, experienced contractor, and extra reserves. Position it as 'here's how we get your first deal done.'" },
    },
  },
  {
    id: "ff_experience_tiers",
    title: "Experience Tiers & Programs",
    type: "concept",
    accentColor: "#d97706",
    content: {
      headline: "More flips = more leverage, better rates, and faster process.",
      thresholds: [
        { range: "10+ Flips", label: "Elite investor", color: "#16a34a", bg: "#f0fdf4", border: "#bbf7d0", desc: "80% ARV / 90% LTC available. Fastest approvals (sometimes table-fund in 5 days). Dedicated account manager. Rate reduction of 0.50–1.00% vs first-timers." },
        { range: "5–9 Flips", label: "Experienced", color: "#2563eb", bg: "#eff6ff", border: "#bfdbfe", desc: "75–80% ARV / 90% LTC. Standard full approval process. Access to new construction programs. Preferred pricing tier." },
        { range: "1–4 Flips", label: "Emerging", color: "#ca8a04", bg: "#fffbeb", border: "#fde68a", desc: "70–75% ARV / 85% LTC. Full approval process. Slightly elevated rate. All programs available. Build track record here." },
        { range: "0 Flips", label: "First-timer", color: "#f97316", bg: "#fff7ed", border: "#fed7aa", desc: "65–70% ARV / 85% LTC max. Higher rate, more documentation, more scrutiny on contractor and SOW. Manageable with right deal structure." },
      ],
      programs: [
        { name: "Fix & Flip — Standard", badge: "Default", color: "#d97706", ltv: "75% ARV", min: "620+ credit · 0+ flips · Entity required", desc: "Purchase + rehab in one loan. 12-month term standard, 6-month extensions available. Always start here." },
        { name: "Fix & Flip — Elite", badge: "Best terms", color: "#16a34a", ltv: "80% ARV", min: "660+ credit · 5+ flips · Lower rate", desc: "Reserved for proven operators. Streamlined process, higher leverage, priority service. Your repeat clients earn this." },
        { name: "Fix & Flip — New Construction", badge: "Ground-up", color: "#2563eb", ltv: "75% ARV", min: "660+ credit · Experienced builder · Plans + permits", desc: "Tear-down or vacant lot. Requires full plans, permits, and experienced GC. Draws tied to construction schedule, not rehab milestones." },
        { name: "Fix to Rent", badge: "BRRRR play", color: "#7c3aed", ltv: "75% ARV", min: "660+ credit · Exit = DSCR refi", desc: "Flip loan with built-in DSCR refi path. Investor flips, stabilizes, then rolls into DSCR. Two-deal relationship in one conversation." },
      ],
      callout: { type: "tip", text: "The Fix to Rent / BRRRR conversation is a relationship-builder. Lead with 'What's your end game — sell or hold?' If they say hold, you just opened a second loan." },
    },
  },
  {
    id: "ff_rates",
    title: "Rate & Fee Structure",
    type: "concept",
    accentColor: "#d97706",
    content: {
      headline: "Fix & flip pricing has more levers than DSCR — know what moves rates and what moves points.",
      paragraphs: [
        "Rates are higher than long-term rentals by design — this is short-term bridge debt with construction risk. Borrowers who don't understand this need education, not a lower quote. The math on a 6-month flip at 10.5% is very different than a 30-year mortgage.",
      ],
      breakdown: [
        { letter: "↑", word: "Origination Points", desc: "Typically 1.5–3 points. Points are the primary profit lever — always quote as a range and explain the trade-off with rate." },
        { letter: "↑", word: "Rate Range", desc: "9.5%–12.5% interest-only is the current market range. Best borrowers at highest leverage land at the low end." },
        { letter: "↓", word: "Experience Discount", desc: "10+ flips can earn 0.50–1.00% rate reduction. A proven track record is literally worth money — use this to retain your best clients." },
        { letter: "↑", word: "Construction Risk", desc: "Ground-up and heavy rehab (gut renovations) price 0.25–0.75% higher than cosmetic flips. The lender is taking on completion risk." },
        { letter: "↑", word: "Draw Fees", desc: "Typically $150–$300 per draw inspection. Usually 3–4 draws per project. Include this in your borrower cost summary — surprises hurt relationships." },
        { letter: "↑", word: "Extension Fees", desc: "If the project runs long, extension fees apply — typically 0.50–1.00% of the loan per 3-month extension. Build this into timelines proactively." },
      ],
      callout: { type: "tip", text: "Always give borrowers a total cost of capital summary, not just the rate: 'At 10.5% on $300K for 9 months plus 2 points, your all-in interest cost is $31,875 plus $6,000 origination. Against a $60K expected profit, your cost of capital is 63% of profit.' That's the number that matters to a flipper." },
    },
  },
  {
    id: "ff_scope_of_work",
    title: "Scope of Work & Construction Risk",
    type: "concept",
    accentColor: "#d97706",
    content: {
      headline: "The SOW is the backbone of the deal — weak scopes cause draw delays, budget overruns, and blown timelines.",
      paragraphs: [
        "The <strong>Scope of Work (SOW)</strong> is the itemized list of every renovation: materials, labor, quantities, and costs. It's reviewed by the lender's underwriter and used by the inspector to verify draw milestones. A vague SOW is the single most common cause of deal delays.",
      ],
      breakdown: [
        { letter: "L", word: "Light Rehab", desc: "Cosmetic only: paint, flooring, fixtures, appliances. Under $30K. Lowest risk, fastest draws, standard rate." },
        { letter: "M", word: "Medium Rehab", desc: "Kitchen and bath updates, HVAC, roof. $30K–$80K. Standard rehab product. Most common fix & flip category." },
        { letter: "H", word: "Heavy Rehab", desc: "Structural work, additions, full gut renovation. $80K+. Requires experienced contractor. Higher rate, more draw scrutiny, longer timeline." },
        { letter: "G", word: "Ground-Up / New Construction", desc: "Complete tear-down or vacant land build. Separate program. Requires full plans, permits, builder experience. Highest complexity and highest margins." },
      ],
      callout: { type: "warning", text: "Borrowers routinely underestimate rehab budgets by 20–30%. If a scope looks too clean, push back: 'Have you walked the property with your contractor yet? Budget surprises mid-project are the #1 reason deals go sideways.' A realistic SOW protects everyone." },
    },
  },
  {
    id: "ff_exit_strategies",
    title: "Exit Strategies",
    type: "concept",
    accentColor: "#d97706",
    content: {
      headline: "Every fix & flip loan needs a primary exit and a backup exit — always ask for both.",
      paragraphs: [
        "The exit strategy is as important as the entry. Lenders want to know exactly how the bridge loan gets repaid. Market conditions change, timelines slip, and buyers fall through — a borrower with only one exit is a borrower who may not repay on time.",
      ],
      breakdown: [
        { letter: "1", word: "Sale (Primary)", desc: "Property sells on the open market post-renovation. Loan paid off at closing. Clean exit. Most common for pure flippers. Depends on retail market conditions." },
        { letter: "2", word: "DSCR Refinance", desc: "Property is rented post-renovation and refinanced into a DSCR long-term loan. The BRRRR strategy. Allows investor to hold the asset and recover equity. Creates a second loan for you." },
        { letter: "3", word: "Conventional Refi", desc: "Owner-occupant investor plans to move in. Refinances into primary residence mortgage. Less common but legitimate exit. Must document intent at origination." },
        { letter: "4", word: "Sale to Another Investor", desc: "Wholesale or off-market sale to another investor post-renovation. Faster than retail but typically at a discount to ARV. Backup option for borrowers in slow markets." },
      ],
      callout: { type: "warning", text: "If a borrower's only exit is 'I'll sell it,' probe harder: 'What's the DOM in that zip code? What if it sits 90 days? Do you have a backup?' Helping them think through the backup isn't pessimism — it's how you save the relationship if the market softens." },
    },
  },
  {
    id: "ff_red_flags",
    title: "Deal Killers & Red Flags",
    type: "concept",
    accentColor: "#d97706",
    content: {
      headline: "Catch these early — before you submit, not after underwriting wastes a week.",
      breakdown: [
        { letter: "✗", word: "ARV Over-Inflation", desc: "Borrower comps cherry-picked from different neighborhoods, superior finishes, or larger square footage. Underwriter will see it. If ARV doesn't hold, LTV blows up." },
        { letter: "✗", word: "No Entity", desc: "Borrower wants to close in personal name. Most programs require LLC. Don't spend time structuring a deal that can't close." },
        { letter: "✗", word: "Contractor Not Licensed / Insured", desc: "Lenders require general contractor to carry liability and workers' comp. Family friend doing the work for cash = no approval." },
        { letter: "✗", word: "Unrealistic Timeline", desc: "Borrower claims they can gut-renovate a 1,500 sq ft house in 6 weeks. Flag it. Overruns mean extension fees or default." },
        { letter: "✗", word: "No Reserves", desc: "Borrower is putting all cash into the down payment with nothing left. No reserves = no draws floated = project stalls. Require verification before submission." },
        { letter: "✗", word: "Property Condition / Title Issues", desc: "Extensive environmental issues, major foundation problems, clouded title, or tax liens can make a property unlendable regardless of ARV." },
      ],
      callout: { type: "tip", text: "Run a 2-minute pre-screen before every submission: entity formed? Contractor lined up with license? Reserves verified? ARV comps pulled? Catching a deal-killer before underwriting is the difference between a 3-day clear-to-close and a 10-day waste." },
    },
  },
  {
    id: "ff_calc",
    title: "Fix & Flip Deal Analyzer",
    type: "calculator",
    accentColor: "#d97706",
    content: {
      headline: "Run the full deal in 30 seconds.",
      intro: "Enter any scenario — profit, loan sizing, LTV checks, and deal verdict update instantly.",
      fields: [
        { key: "purchase",    label: "Purchase Price ($)",     default: 180000, step: 5000 },
        { key: "rehab",       label: "Rehab Budget ($)",       default: 45000,  step: 2500 },
        { key: "arv",         label: "ARV ($)",                default: 320000, step: 5000 },
        { key: "rate",        label: "Rate (%)",               default: 10.5,   step: 0.25, min: 7 },
        { key: "months",      label: "Hold (months)",          default: 9,      step: 1, min: 1 },
        { key: "points",      label: "Origination Points",     default: 2,      step: 0.5, min: 0 },
        { key: "sellCosts",   label: "Selling Costs (%)",      default: 7,      step: 0.5, min: 0 },
        { key: "experience",  label: "Flips Completed",        default: 3,      step: 1, min: 0 },
        { key: "credit",      label: "Credit Score",           default: 700,    step: 10, min: 500 },
      ],
      compute(v) {
        const totalCost    = v.purchase + v.rehab;
        const ltvARV       = totalCost / v.arv;
        const ltcPct       = totalCost / totalCost;
        const maxByARV     = v.arv * 0.75;
        const maxByLTC     = totalCost * 0.90;
        const loanAmt      = Math.min(maxByARV, maxByLTC);
        const downPayment  = totalCost - loanAmt;
        const interest     = loanAmt * (v.rate / 100 / 12) * v.months;
        const origFees     = loanAmt * (v.points / 100);
        const sellCostAmt  = v.arv * (v.sellCosts / 100);
        const totalCostAll = totalCost + interest + origFees + sellCostAmt;
        const profit       = v.arv - totalCostAll;
        const roi          = downPayment > 0 ? (profit / (downPayment + origFees)) * 100 : 0;
        const arvLTV       = loanAmt / v.arv * 100;
        const spread       = v.arv - totalCost;
        let tier, tierColor;
        if (v.experience >= 10)     { tier = "Elite (10+ flips)";       tierColor = "#16a34a"; }
        else if (v.experience >= 5) { tier = "Experienced (5–9 flips)"; tierColor = "#2563eb"; }
        else if (v.experience >= 1) { tier = "Emerging (1–4 flips)";    tierColor = "#ca8a04"; }
        else                        { tier = "First-timer (0 flips)";   tierColor = "#f97316"; }
        const dealVerdict = profit > 30000 && arvLTV <= 75 && spread / v.arv > 0.20
          ? { label: "Strong deal", color: "#16a34a" }
          : profit > 15000 && arvLTV <= 75
          ? { label: "Marginal — check timeline", color: "#ca8a04" }
          : { label: "Weak deal — revisit numbers", color: "#dc2626" };
        return { loanAmt, downPayment, interest, origFees, sellCostAmt, profit, roi, arvLTV, tier, tierColor, dealVerdict, totalCostAll };
      },
      renderResults(r) {
        return {
          highlight: { label: "Net Profit", value: `$${Math.round(r.profit).toLocaleString()}`, color: r.profit > 0 ? "#16a34a" : "#dc2626", barPct: Math.max(0, Math.min(r.profit / 80000, 1)) * 100, barLabels: ["$0", "$40K", "$80K+"] },
          rows: [
            { label: "Max Loan",        value: `$${Math.round(r.loanAmt).toLocaleString()}` },
            { label: "ARV LTV",         value: `${r.arvLTV.toFixed(1)}%`, color: r.arvLTV <= 75 ? "#16a34a" : "#dc2626" },
            { label: "Down Payment",    value: `$${Math.round(r.downPayment).toLocaleString()}` },
            { label: "Total Interest",  value: `$${Math.round(r.interest).toLocaleString()}` },
            { label: "Origination",     value: `$${Math.round(r.origFees).toLocaleString()}` },
            { label: "Selling Costs",   value: `$${Math.round(r.sellCostAmt).toLocaleString()}` },
            { label: "ROI on Cash In",  value: `${r.roi.toFixed(1)}%`, color: r.roi > 40 ? "#16a34a" : r.roi > 15 ? "#ca8a04" : "#dc2626" },
            { label: "Experience Tier", value: r.tier, color: r.tierColor },
            { label: "Deal Verdict",    value: r.dealVerdict.label, color: r.dealVerdict.color },
          ],
          tip: r.dealVerdict.label,
        };
      },
    },
  },
  {
    id: "ff_quiz",
    title: "Knowledge Check",
    type: "quiz",
    accentColor: "#d97706",
    content: {
      passPct: 0.75,
      questions: [
        {
          id: "ffq1",
          question: "A property has a purchase price of $160K, rehab budget of $40K, and ARV of $280K. What is the maximum loan amount (75% ARV or 90% LTC, whichever is less)?",
          options: ["$210,000", "$180,000", "$180,000 (LTC binding)", "$210,000 (ARV binding)"],
          correct: 2,
          explanation: "75% × $280K ARV = $210K. 90% × $200K total cost = $180K. The lesser of the two applies — $180K is the cap. LTC is the binding constraint here.",
        },
        {
          id: "ffq2",
          question: "A borrower has completed 7 fix & flip projects in the last 3 years. Which experience tier do they fall into?",
          options: ["First-timer", "Emerging (1–4 flips)", "Experienced (5–9 flips)", "Elite (10+ flips)"],
          correct: 2,
          explanation: "5–9 completed flips = Experienced tier. This unlocks 75–80% ARV leverage, preferred pricing, and access to new construction programs.",
        },
        {
          id: "ffq3",
          question: "What is the primary purpose of the rehab holdback structure?",
          options: [
            "To reduce the lender's origination revenue",
            "To fund draws as work is completed and verified — protecting both lender and borrower",
            "To require the borrower to contribute more equity",
            "To allow the lender to cancel the loan if costs increase",
          ],
          correct: 1,
          explanation: "The holdback releases funds in stages as verified work is completed. This prevents over-disbursement on incomplete work, protects the lender's collateral, and gives the borrower accountability checkpoints.",
        },
        {
          id: "ffq4",
          question: "A first-time flipper submits a deal. Their contractor is a family friend who works for cash and has no license. What do you do?",
          options: [
            "Submit anyway — the underwriter will figure it out",
            "Reduce the loan amount to compensate for the risk",
            "Do not submit — require a licensed, insured contractor before proceeding",
            "Approve the SOW but flag the contractor in the notes",
          ],
          correct: 2,
          explanation: "Unlicensed, uninsured contractors are an automatic deal-killer for most lenders. Submitting with known issues wastes everyone's time and damages your relationship with the lender. Fix it first.",
        },
        {
          id: "ffq5",
          question: "An investor says their only exit strategy is 'I'll sell it.' What should you do?",
          options: [
            "Accept it — selling is the standard exit for fix & flip",
            "Ask what their backup plan is if it doesn't sell quickly, and discuss DSCR refi as an alternative",
            "Require a signed purchase agreement before closing the loan",
            "Decline the loan — single-exit borrowers don't qualify",
          ],
          correct: 1,
          explanation: "A single exit is acceptable, but a good originator probes for the backup. Market conditions change, buyers fall through, and timelines slip. Understanding the backup plan helps you structure the right term and positions you for a DSCR refi if they end up holding.",
        },
        {
          id: "ffq6",
          question: "Why does 'heavy rehab' (gut renovation) price higher than a cosmetic flip?",
          options: [
            "Regulatory requirements for renovations over $80K",
            "The lender faces construction completion risk — more work means more ways the project can stall",
            "Cosmetic flips are owner-occupied; heavy rehabs are investor-only",
            "Heavy rehab always requires a longer loan term",
          ],
          correct: 1,
          explanation: "The rate premium for heavy rehab reflects completion risk. A cosmetic flip can be abandoned mid-project with the property still sellable. A gut renovation mid-project is unlendable, unmarketable, and a potential loss for the lender.",
        },
      ],
    },
  },
];
// ── tm_multifamily: Multifamily 1–4 Unit Financing ───────────────────────────
export const tm_multifamily = [
  {
    id: "mf_intro",
    title: "Why 1–4 Units Is Its Own Category",
    type: "concept",
    accentColor: "#2563eb",
    content: {
      headline: "One to four units lives at the intersection of residential lending and investment strategy — the rules are different, and so is the conversation.",
      paragraphs: [
        "A <strong>1–4 unit residential property</strong> is the most versatile asset class in real estate lending. The same duplex can be owner-occupied with rental income helping qualify the mortgage, a pure investment property on a DSCR loan, or a house-hack where the buyer lives in one unit and rents the others.",
        "The financing path changes entirely based on <strong>occupancy intent</strong>. Owner-occupied 1–4 unit properties access conventional, FHA, and VA guidelines. Non-owner investment properties go through DSCR or portfolio products. Same physical asset — two completely different loan universes.",
        "Your job as an originator is to identify occupancy intent in the first 60 seconds of every conversation. That one question determines everything that follows.",
      ],
      callout: { type: "tip", text: "First question every time: 'Are you planning to live in the property?' If yes → conventional or FHA path. If no → DSCR or portfolio. Don't spend 20 minutes gathering docs for the wrong product." },
    },
  },
  {
    id: "mf_occupancy",
    title: "Occupancy Types & Loan Paths",
    type: "concept",
    accentColor: "#2563eb",
    content: {
      headline: "Occupancy is the fork in the road — every qualification rule flows from this single determination.",
      paragraphs: [
        "Lenders define occupancy in three categories, and misrepresenting occupancy is mortgage fraud. Always document intent carefully and never coach a borrower to claim owner-occupancy for a property they don't intend to live in.",
      ],
      breakdown: [
        { letter: "OO", word: "Owner-Occupied", desc: "Borrower lives in one unit as primary residence. Access to conventional (Fannie/Freddie), FHA (3.5% down), VA (0% down for eligible). Rental income from other units can offset qualifying payment. Best rates." },
        { letter: "2H", word: "Second Home / Vacation", desc: "Single-family or 1-unit only. Borrower uses property part-time. Higher rate than OO, lower rate than investment. Restrictions: must be 1 unit, not rented on a regular basis. Rarely applies to 2–4 unit." },
        { letter: "IP", word: "Investment Property", desc: "Borrower does not live there. Applies to all pure rental scenarios. Higher rates, higher down payment (15–25%), stricter reserves. DSCR and portfolio products live here." },
      ],
      callout: { type: "warning", text: "The occupancy fraud risk is highest on 2–4 unit properties where buyers try to claim owner-occupancy to get FHA rates on what is clearly a rental acquisition. Document their stated intent, verify plausibility, and never coach the answer." },
    },
  },
  {
    id: "mf_conventional_oo",
    title: "Owner-Occupied: Conventional Path",
    type: "concept",
    accentColor: "#2563eb",
    content: {
      headline: "Fannie Mae and Freddie Mac guidelines govern owner-occupied 1–4 unit purchases — understand the rental income rules cold.",
      paragraphs: [
        "When a borrower occupies one unit of a 2–4 unit property, they get access to conventional financing at near-primary-residence terms. The game-changing benefit: <strong>rental income from the other units</strong> can be used to qualify — reducing or eliminating the net monthly obligation in DTI.",
        "This is the most underutilized product in multifamily lending. A borrower who can't qualify for a $500K single-family home may easily qualify for a $600K triplex because two rental units cover the mortgage payment.",
      ],
      breakdown: [
        { letter: "↓", word: "Down Payment", desc: "5% minimum for 2-unit owner-occupied conventional. 3.5% with FHA. 25% for investment (non-OO). The OO advantage is massive on leverage." },
        { letter: "↑", word: "Rental Income — Existing", desc: "75% of market rents from non-occupied units counted as qualifying income. Haircut accounts for vacancy. Documented by appraisal's 'comparable rents' schedule." },
        { letter: "↑", word: "Rental Income — Purchase", desc: "If borrower has no landlord history, lender may require 6 months PITI in reserves as a condition of using rental income. Know your lender overlay." },
        { letter: "↓", word: "DTI", desc: "Conventional max 45–50% DTI with strong compensating factors. FHA up to 56.9% with AUS approval. Rental income often swings DTI from failing to passing." },
        { letter: "↑", word: "Reserves", desc: "2–6 months PITI required depending on units and credit profile. More units = more reserves. Investment property path requires 6 months minimum." },
      ],
      formula: { label: "Net Rental Income Calculation", expression: "Qualifying Rental = (Market Rent per Unit × # Non-Occupied Units) × 75%", sub: "Example: 2 units @ $1,400/mo each → $1,400 × 2 × 75% = $2,100/mo qualifying income added to borrower's income" },
      callout: { type: "tip", text: "Run the rental income numbers before quoting any owner-occupied 2–4 unit deal. The borrower's qualifying picture changes dramatically once you add in those rents. This is your ace card for borrowers who think they can't afford investment real estate." },
    },
  },
  {
    id: "mf_fha",
    title: "FHA Financing for 2–4 Units",
    type: "concept",
    accentColor: "#2563eb",
    content: {
      headline: "FHA's 3.5% down on a 4-unit property is one of the most powerful wealth-building tools in lending — most borrowers don't know it exists.",
      paragraphs: [
        "FHA allows owner-occupied purchases on 1–4 unit properties with as little as 3.5% down (580+ credit) or 10% down (500–579 credit). This means a borrower can acquire a fourplex — a $800K asset — with $28,000 down and use the rental income from three units to cover most or all of the mortgage payment.",
        "The catch: FHA has <strong>loan limits by county</strong> that cap the purchase price. In high-cost markets, the limits are higher but may still constrain larger multifamily buys.",
      ],
      breakdown: [
        { letter: "3.5%", word: "Minimum Down (580+ FICO)", desc: "Lowest entry point in residential investment lending. Requires MIP (mortgage insurance premium) upfront and monthly — factor this into the total payment." },
        { letter: "MIP", word: "Mortgage Insurance", desc: "FHA charges 1.75% upfront MIP (typically rolled into loan) plus 0.55%–0.85% annual MIP monthly. Cannot be removed on 30-yr loans with <10% down. This is the trade-off for low down payment." },
        { letter: "SH", word: "Self-Sufficiency Test (3–4 units)", desc: "For 3 and 4-unit FHA purchases, the property's rental income (75% of all units including the owner's unit) must equal or exceed the full PITIA. If the rents don't cover the payment, FHA won't approve." },
        { letter: "LL", word: "Loan Limits", desc: "FHA limits vary by county and unit count. A 4-unit in a standard county: ~$1.34M. A 4-unit in a high-cost county: up to $2.21M. Always look up current limits before quoting." },
      ],
      callout: { type: "tip", text: "The FHA self-sufficiency test on 3–4 units is a screening tool, not an obstacle. Run it first: '75% of all rents ≥ PITIA?' If yes, you have a deal. If no, the property doesn't cash flow well enough for FHA — pivot to conventional or a larger down payment." },
    },
  },
  {
    id: "mf_va",
    title: "VA Loans for 2–4 Units",
    type: "concept",
    accentColor: "#2563eb",
    content: {
      headline: "0% down on a fourplex — the most powerful house-hack tool available, and almost nobody talks about it.",
      paragraphs: [
        "Eligible veterans and active service members can purchase a 1–4 unit property with <strong>zero down payment</strong> using their VA entitlement, as long as they occupy one unit as their primary residence. This is not a niche scenario — it is a legitimate, underused wealth-building strategy that you should be presenting to every eligible borrower who mentions multifamily.",
        "VA has no mortgage insurance premium, competitive rates, and no loan limits for borrowers with full entitlement. The only hard requirement is that the veteran must occupy one unit.",
      ],
      breakdown: [
        { letter: "0%", word: "Down Payment", desc: "No down payment required for eligible borrowers with full entitlement. Funding fee applies (typically 2.15% first use, can be financed into the loan)." },
        { letter: "OO", word: "Occupancy Required", desc: "Veteran must occupy one unit within 60 days of closing. This is non-negotiable. If intent is purely investment with no occupancy, VA does not apply." },
        { letter: "FU", word: "Funding Fee", desc: "1.25–3.3% of loan amount depending on down payment and usage. Replaces MIP. Exempt for veterans with service-connected disability." },
        { letter: "RI", word: "Rental Income", desc: "Income from non-occupied units can qualify via VA guidelines — similar to conventional 75% haircut approach. Helps significantly with DTI on 2–4 unit deals." },
      ],
      callout: { type: "tip", text: "Every pre-qualification for a veteran should include the question: 'Have you considered a 2–4 unit property?' Buying a duplex at 0% down and having a tenant cover half the mortgage is a better financial outcome than buying a single-family home for the same price. This conversation makes careers." },
    },
  },
  {
    id: "mf_dscr_investment",
    title: "Investment 1–4 Units: DSCR Path",
    type: "concept",
    accentColor: "#2563eb",
    content: {
      headline: "For non-owner investors, DSCR is the default product — no income docs, no DTI, just the property's cash flow.",
      paragraphs: [
        "When the borrower is not living in the property, conventional Fannie/Freddie guidelines no longer apply at their best terms. The borrower enters investment property territory: higher down payment, tighter reserve requirements, and rate add-ons for investment status.",
        "DSCR loans bypass the income documentation problem entirely for these investors. The property qualifies itself based on rental income vs. PITIA — the same framework you've already learned. For a 2–4 unit investment property, DSCR is almost always the right starting point.",
      ],
      breakdown: [
        { letter: "SFR", word: "1 Unit — Single Family", desc: "DSCR 1-4 standard product. Simplest underwrite. Single tenant, single lease. DSCR = total rent ÷ PITIA." },
        { letter: "DPX", word: "2 Unit — Duplex", desc: "DSCR uses combined rent from both units. Must document both leases or appraiser's market rents. Strong cash flow asset class — two income streams, one PITIA." },
        { letter: "TRI", word: "3 Unit — Triplex", desc: "Combined rents vs. PITIA. More income diversification — one vacancy doesn't kill the DSCR. Slightly higher complexity in underwriting." },
        { letter: "4PX", word: "4 Unit — Fourplex", desc: "Highest income potential in residential lending. Combined rents vs. PITIA. One vacancy = 25% income loss — factor into DSCR sensitivity. Appraisal uses income approach, not just comp approach." },
      ],
      formula: { label: "DSCR for Multi-Unit", expression: "DSCR = (Unit 1 Rent + Unit 2 Rent + ... ) ÷ PITIA", sub: "Use actual leases if occupied; appraiser's market rents if vacant or if leases are below market" },
      callout: { type: "warning", text: "For 2–4 unit DSCR deals, always verify whether leases are at market rent. A long-term tenant paying $800/mo in a $1,400/mo market will drag DSCR below 1.0 even if the property should cash flow. The solution: use market rents from appraisal if the spread is significant and the lender allows it." },
    },
  },
  {
    id: "mf_ltv_programs",
    title: "LTV Tiers & Program Matrix",
    type: "concept",
    accentColor: "#2563eb",
    content: {
      headline: "Occupancy and unit count together determine your LTV ceiling — know the matrix before you quote.",
      thresholds: [
        { range: "OO 2-unit: 90–95% LTV", label: "Conventional / FHA", color: "#16a34a", bg: "#f0fdf4", border: "#bbf7d0", desc: "Best leverage available. Owner-occupied duplex with strong credit. 5% down conventional or 3.5% FHA. Rental income from Unit 2 offsets payment." },
        { range: "OO 3–4 unit: 96.5% LTV", label: "FHA only", color: "#2563eb", bg: "#eff6ff", border: "#bfdbfe", desc: "FHA 3.5% down is the max leverage play for 3–4 unit owner-occupied. Must pass self-sufficiency test. Conventional max is 75–80% LTV for 3–4 unit OO." },
        { range: "Investment 1-unit: 85% LTV", label: "DSCR / conventional", color: "#ca8a04", bg: "#fffbeb", border: "#fde68a", desc: "Single-family investment. DSCR at 85% LTV with 1.25+ DSCR. Conventional investment at 85% LTV with income qualification. Lower LTV = better rate." },
        { range: "Investment 2–4 unit: 75–80% LTV", label: "DSCR / portfolio", color: "#f97316", bg: "#fff7ed", border: "#fed7aa", desc: "25% down minimum for most investment 2–4 unit products. DSCR 1-4 program applies. Some lenders go to 80% for strong DSCR and credit." },
      ],
      programs: [
        { name: "Conventional OO 2-unit", badge: "Best rates", color: "#16a34a", ltv: "95% LTV", min: "620+ credit · OO · Rental income from Unit 2 eligible", desc: "Fannie/Freddie guidelines. 5% down, no MIP if 20%+ down. Rental income from non-occupied units helps DTI." },
        { name: "FHA 2–4 Unit", badge: "Lowest down", color: "#2563eb", ltv: "96.5% LTV", min: "580+ credit · OO · Self-sufficiency test for 3–4 units", desc: "3.5% down with MIP. Most powerful entry point for first-time multifamily buyers. Loan limits apply by county." },
        { name: "VA 2–4 Unit", badge: "0% down", color: "#7c3aed", ltv: "100% LTV", min: "Eligible veteran · OO · Funding fee applies", desc: "Zero down, no MIP. Veteran must occupy. Best product in lending for eligible borrowers. Severely underutilized." },
        { name: "DSCR 1-4 Investment", badge: "No income docs", color: "#ca8a04", ltv: "80% LTV", min: "660+ credit · DSCR ≥ 1.0 · Non-OO", desc: "Combined unit rents vs. PITIA. No DTI, no tax returns. Default product for non-owner investors on 1–4 unit residential." },
        { name: "Portfolio / Non-QM", badge: "Flexible", color: "#f97316", ltv: "75% LTV", min: "600+ credit · Asset depletion or bank statement OK", desc: "For investors who don't fit DSCR (low rents) or conventional (complex income). Higher rate, flexible docs. Lender holds in portfolio." },
      ],
      callout: { type: "tip", text: "Build a quick cheat sheet: OO + FHA = 3.5% down. OO + VA = 0% down. OO + Conventional = 5% down. Investment = 20–25% down. Knowing this cold lets you answer 'how much do I need?' in 10 seconds on any call." },
    },
  },
  {
    id: "mf_rental_income",
    title: "Rental Income: The Universal Qualifier",
    type: "concept",
    accentColor: "#2563eb",
    content: {
      headline: "How rental income is counted changes the entire qualifying picture — and the rules differ by product.",
      paragraphs: [
        "Every 1–4 unit product handles rental income differently. Getting this wrong means either killing a deal that should close or approving a deal that blows up in underwriting. Master these four scenarios.",
      ],
      breakdown: [
        { letter: "1", word: "Conventional — Subject Property Rents", desc: "75% of market rents (from appraisal Form 1007) added to income for non-occupied units. Borrower needs 2-year landlord history OR 6 months PITI reserves as compensating factor. Most common scenario." },
        { letter: "2", word: "FHA — Rental Offset", desc: "Rental income from non-owner units can offset the mortgage payment (reduce the net payment in DTI). Must be documented by appraisal or signed leases. Unique to FHA — called 'net rental income' treatment." },
        { letter: "3", word: "DSCR — 100% Gross Rents", desc: "All unit rents combined. No DTI — income not used at all. DSCR ratio is the sole test. Use actual leases if in place; appraiser's comparable rents if vacant or below market." },
        { letter: "4", word: "Departing Residence — Existing Rental", desc: "If borrower previously lived somewhere and is converting it to rental, 75% of the market rent can offset the old mortgage payment in DTI. Requires signed lease or documented equity of 25%+ in the departing property." },
      ],
      callout: { type: "warning", text: "The 75% haircut is universal but the source of the rents matters. Conventional and FHA use the appraiser's Form 1007 for market rents if no lease exists. DSCR uses comparable rents from the appraisal. If a borrower has below-market leases, the appraisal rent schedule becomes critical — and some lenders will use actual lease rent, not market. Know your lender's policy before quoting." },
    },
  },
  {
    id: "mf_reserves",
    title: "Reserve Requirements",
    type: "concept",
    accentColor: "#2563eb",
    content: {
      headline: "Reserves are the most frequently overlooked qualifying factor — and the most common reason strong borrowers get declined.",
      paragraphs: [
        "Reserves are liquid assets the borrower retains after closing. They are not the down payment — they are additional funds held in reserve to demonstrate the borrower can weather vacancies, repairs, and payment interruptions. Requirements escalate with unit count and investment status.",
      ],
      breakdown: [
        { letter: "OO", word: "Owner-Occupied 1–2 Unit", desc: "Typically 2 months PITI. May increase to 4–6 months with lower credit score or higher DTI as a compensating factor overlay." },
        { letter: "OO", word: "Owner-Occupied 3–4 Unit", desc: "6 months PITI standard for FHA on 3–4 unit. Conventional may require 6 months as well. The additional complexity of managing tenants warrants higher buffer." },
        { letter: "IP", word: "Investment 1–4 Unit (DSCR)", desc: "6–12 months PITIA common on DSCR products. Some lenders require 3–6 months per property for borrowers with multiple financed properties. Portfolio borrowers face the strictest reserve requirements." },
        { letter: "MF", word: "Multiple Financed Properties", desc: "Fannie Mae: if borrower has 1–6 financed properties, standard reserves. 7–10 financed properties: 2% of outstanding balance of all non-subject financed properties held as additional reserves. This surprises experienced investors — know it." },
      ],
      callout: { type: "tip", text: "Ask about reserves before you ask about income. 'Do you have liquid assets beyond the down payment and closing costs?' A borrower who is cleaned out after closing won't qualify regardless of their income. Find out early." },
    },
  },
  {
    id: "mf_rate_adjustments",
    title: "Rate Adjustments for 1–4 Units",
    type: "concept",
    accentColor: "#2563eb",
    content: {
      headline: "Every deviation from a single-family, owner-occupied, high-credit purchase costs basis points — know the stack.",
      paragraphs: [
        "Conventional loans use Loan Level Price Adjustments (LLPAs) — a grid of add-ons layered on the base rate based on risk factors. For multifamily investment properties, these add-ons stack quickly. Understanding them lets you explain pricing honestly and find ways to optimize.",
      ],
      breakdown: [
        { letter: "↑", word: "Investment Property Status", desc: "+0.50–1.25% vs owner-occupied, depending on LTV and credit. This is the biggest single factor. Borrowers moving from OO to investment pricing are often shocked." },
        { letter: "↑", word: "2–4 Unit Property Type", desc: "+0.25–0.50% vs single-family for conventional investment. Lender sees more complexity: multiple tenants, more maintenance, higher vacancy risk." },
        { letter: "↑", word: "LTV", desc: "Every LTV tier above 60% adds cost. Investment at 75% LTV costs more than 70%. Show borrowers the rate improvement from paying down 5% more." },
        { letter: "↑", word: "Credit Score Brackets", desc: "740+ = best pricing. Each 20-point bracket below 740 adds cost. 660–679 is meaningfully more expensive than 700–719. Credit improvement before closing is worth discussing." },
        { letter: "↓", word: "Buydown / Points", desc: "Borrowers can pay points to reduce rate. On a 30-year hold with stable rents, buying down the rate often pencils. On a short hold or BRRRR-to-refi, it rarely does." },
        { letter: "↑", word: "Cash-Out Refinance", desc: "+0.375–0.75% vs rate-term refi. Investment property cash-out is the most expensive conventional scenario. DSCR products may be competitive here." },
      ],
      callout: { type: "tip", text: "When a borrower is near a credit score bracket threshold — say, 698 — it's worth the conversation: 'If we can get you to 700 before we lock, you'll save approximately X basis points over the life of the loan.' That's real money and shows you're working for them." },
    },
  },
  {
    id: "mf_househack",
    title: "The House-Hack Conversation",
    type: "concept",
    accentColor: "#2563eb",
    content: {
      headline: "House-hacking is the most accessible path to real estate wealth — and it's your job to introduce it.",
      paragraphs: [
        "A <strong>house-hack</strong> is when an owner-occupant purchases a 2–4 unit property, lives in one unit, and rents the others — using tenant income to offset or eliminate their housing cost. Done correctly, the borrower lives for free (or near free) while building equity in an investment asset.",
        "The math is compelling. A borrower stretching to afford a $450K single-family primary residence may qualify more easily for a $550K duplex where one unit rents for $1,800/mo — effectively cutting their net housing cost in half. The duplex is a better financial decision in almost every scenario.",
      ],
      breakdown: [
        { letter: "1", word: "Identify the Candidate", desc: "First-time buyers, buyers who mention 'I want to invest eventually,' buyers in high-rent markets, buyers with flexible living preferences. These are your house-hack conversations." },
        { letter: "2", word: "Run the OO Numbers", desc: "Qualify them for the 2–4 unit at owner-occupied rates with rental income counted. Show them the net monthly cost after rental offset. This is often a revelation." },
        { letter: "3", word: "Show the Wealth Math", desc: "Year 1: reduced housing cost. Year 3: move out, convert to full rental. Year 5: DSCR refi to pull equity. That's three transactions you can be part of." },
        { letter: "4", word: "Address the Lifestyle Objection", desc: "Many borrowers are reluctant because they don't want to be a landlord. Reframe: 'You're not a landlord yet — you're a neighbor who collects a check. You scale up when you're ready.'" },
      ],
      callout: { type: "tip", text: "The house-hack conversation is a long-game relationship builder. The borrower who buys a duplex at 25 with your help will refinance it, do a cash-out, buy a fourplex, and send you three referrals by the time they're 32. Learn to have this conversation well and it will define your business." },
    },
  },
  {
    id: "mf_calc",
    title: "Multifamily Deal Analyzer",
    type: "calculator",
    accentColor: "#2563eb",
    content: {
      headline: "Run any 1–4 unit scenario: OO, house-hack, or investment.",
      intro: "Adjust inputs — qualifying income, DSCR, net monthly cost, and recommended program update instantly.",
      fields: [
        { key: "purchasePrice",  label: "Purchase Price ($)",      default: 520000, step: 10000 },
        { key: "units",          label: "Number of Units",          default: 2,      step: 1, min: 1, max: 4 },
        { key: "rentPerUnit",    label: "Rent Per Unit ($/mo)",     default: 1800,   step: 50 },
        { key: "ownerOccupied",  label: "Owner-Occupied? (1=Yes)",  default: 1,      step: 1, min: 0, max: 1 },
        { key: "downPct",        label: "Down Payment (%)",         default: 5,      step: 1, min: 0 },
        { key: "rate",           label: "Interest Rate (%)",        default: 7.25,   step: 0.125, min: 3 },
        { key: "taxes",          label: "Property Taxes/mo ($)",    default: 550,    step: 50 },
        { key: "insurance",      label: "Insurance/mo ($)",         default: 180,    step: 25 },
        { key: "hoa",            label: "HOA/mo ($)",               default: 0,      step: 50 },
        { key: "credit",         label: "Credit Score",             default: 720,    step: 10, min: 500 },
        { key: "borrowerIncome", label: "Borrower Income/mo ($)",   default: 9500,   step: 250 },
      ],
      compute(v) {
        const loanAmt   = v.purchasePrice * (1 - v.downPct / 100);
        const mo        = v.rate / 100 / 12;
        const n         = 360;
        const pi        = mo > 0 ? loanAmt * (mo * Math.pow(1 + mo, n)) / (Math.pow(1 + mo, n) - 1) : loanAmt / n;
        const pitia     = pi + v.taxes + v.insurance + v.hoa;
        const rentalUnits     = v.ownerOccupied ? v.units - 1 : v.units;
        const grossRent       = rentalUnits * v.rentPerUnit;
        const qualifyingRent  = grossRent * 0.75;
        const dscr            = pitia > 0 ? (v.units * v.rentPerUnit) / pitia : 0;
        const netHousingCost  = v.ownerOccupied ? pitia - qualifyingRent : pitia;
        const totalDebt       = pitia;
        const dti             = v.ownerOccupied
          ? ((totalDebt - qualifyingRent) / v.borrowerIncome) * 100
          : (totalDebt / v.borrowerIncome) * 100;

        let program, programColor;
        if (v.ownerOccupied && v.downPct <= 3.5 && v.credit >= 580) {
          program = "FHA — 3.5% down · OO"; programColor = "#2563eb";
        } else if (v.ownerOccupied && v.downPct <= 5 && v.credit >= 620) {
          program = "Conventional OO — 5% down"; programColor = "#16a34a";
        } else if (v.ownerOccupied && v.downPct === 0) {
          program = "VA — 0% down (if eligible)"; programColor = "#7c3aed";
        } else if (!v.ownerOccupied && dscr >= 1.0 && v.credit >= 660) {
          program = "DSCR 1-4 Investment"; programColor = "#ca8a04";
        } else if (!v.ownerOccupied && dscr < 1.0 && v.credit >= 660) {
          program = "No Ratio / Portfolio"; programColor = "#f97316";
        } else {
          program = "Review — borderline qualification"; programColor = "#dc2626";
        }

        const dscrColor = dscr >= 1.25 ? "#16a34a" : dscr >= 1.0 ? "#ca8a04" : "#dc2626";
        const dtiColor  = dti <= 36 ? "#16a34a" : dti <= 45 ? "#ca8a04" : "#dc2626";

        return { loanAmt, pi, pitia, grossRent, qualifyingRent, dscr, dscrColor, netHousingCost, dti, dtiColor, program, programColor };
      },
      renderResults(r) {
        return {
          highlight: { label: "DSCR", value: r.dscr.toFixed(2), color: r.dscrColor, barPct: Math.min(r.dscr / 2, 1) * 100, barLabels: ["0", "1.0 floor", "2.0+"] },
          rows: [
            { label: "Loan Amount",        value: `$${Math.round(r.loanAmt).toLocaleString()}` },
            { label: "Full PITIA",         value: `$${Math.round(r.pitia).toLocaleString()}` },
            { label: "Qualifying Rent",    value: `$${Math.round(r.qualifyingRent).toLocaleString()}/mo (75%)` },
            { label: "Net Housing Cost",   value: `$${Math.round(r.netHousingCost).toLocaleString()}/mo`, color: r.netHousingCost < 500 ? "#16a34a" : undefined },
            { label: "Gross Rent (all)",   value: `$${Math.round(r.grossRent).toLocaleString()}/mo` },
            { label: "DTI (est.)",         value: `${r.dti.toFixed(1)}%`, color: r.dtiColor },
            { label: "Recommended Program", value: r.program, color: r.programColor },
          ],
          tip: r.program,
        };
      },
    },
  },
  {
    id: "mf_quiz",
    title: "Knowledge Check",
    type: "quiz",
    accentColor: "#2563eb",
    content: {
      passPct: 0.75,
      questions: [
        {
          id: "mfq1",
          question: "A borrower wants to purchase a duplex and live in one unit. Which of the following is TRUE about their financing options?",
          options: [
            "They must use investment property guidelines — 25% down minimum",
            "They can access owner-occupied conventional financing with as little as 5% down",
            "FHA does not apply to 2-unit properties",
            "Rental income from Unit 2 cannot be counted for qualification",
          ],
          correct: 1,
          explanation: "Owner-occupied 2-unit properties qualify for conventional financing at 5% down. FHA applies at 3.5% down. Rental income from the non-occupied unit can be counted at 75% of market rent to help with DTI.",
        },
        {
          id: "mfq2",
          question: "A veteran wants to purchase a fourplex with no money down. They plan to live in Unit 1 and rent the other three. Which product applies?",
          options: [
            "FHA — 3.5% down is the minimum for 4-unit",
            "VA loan — 0% down with occupancy of one unit",
            "DSCR — no income docs needed",
            "Conventional — available at 0% down for veterans",
          ],
          correct: 1,
          explanation: "VA loans allow eligible veterans to purchase 1–4 unit properties with zero down payment, as long as they occupy one unit as their primary residence. This is one of the most powerful products in lending.",
        },
        {
          id: "mfq3",
          question: "A triplex generates $4,500/mo in gross rents. PITIA is $3,800/mo. What is the DSCR, and does it qualify for the standard DSCR 1-4 product?",
          options: [
            "DSCR 1.18 — yes, qualifies at DSCR ≥ 1.0",
            "DSCR 0.84 — no, below the 1.0 floor",
            "DSCR 1.18 — no, multi-unit requires 1.25+",
            "DSCR 1.42 — yes, after the 75% haircut is applied",
          ],
          correct: 0,
          explanation: "$4,500 ÷ $3,800 = 1.184. DSCR for the investment loan uses gross rents divided by full PITIA — no 75% haircut applies in DSCR underwriting. 1.18 ≥ 1.0, so it qualifies for the standard DSCR 1-4 program.",
        },
        {
          id: "mfq4",
          question: "FHA requires a 'self-sufficiency test' on 3–4 unit purchases. What does this test check?",
          options: [
            "That the borrower's income exceeds the total PITIA",
            "That 75% of all unit rents (including the owner's unit) equals or exceeds the full PITIA",
            "That the property has been rented for at least 12 months",
            "That the borrower has 6 months reserves for each unit",
          ],
          correct: 1,
          explanation: "The FHA self-sufficiency test requires that 75% of the gross rents from ALL units — including the unit the owner will occupy — equals or exceeds the full PITIA payment. If rents don't support the payment at 75%, FHA won't approve the 3–4 unit purchase.",
        },
        {
          id: "mfq5",
          question: "A DSCR borrower has below-market leases — tenants paying $900/mo in units appraising at $1,400/mo market rent. Which approach is most favorable for qualification?",
          options: [
            "Always use the actual lease rent — lenders never allow market rents",
            "Use market rents from the appraisal's comparable rents schedule if lender guidelines permit",
            "Average the actual and market rents",
            "Disregard rents below 80% of market and use market rents automatically",
          ],
          correct: 1,
          explanation: "Many DSCR lenders will use the appraiser's market rent schedule rather than below-market leases. This is lender-specific — some use actual leases, some use the greater of lease or market. Always check your lender's policy and always get an appraisal with a rent schedule on multi-unit DSCR deals.",
        },
        {
          id: "mfq6",
          question: "A borrower currently owns 8 financed properties and wants to purchase a 9th. What reserve requirement should you be aware of under Fannie Mae guidelines?",
          options: [
            "No additional reserves — standard requirements apply",
            "12 months PITIA on the subject property only",
            "2% of the outstanding balance of all non-subject financed properties held as additional reserves",
            "Fannie Mae does not allow more than 7 financed properties",
          ],
          correct: 2,
          explanation: "Fannie Mae allows up to 10 financed properties, but borrowers with 7–10 financed properties must hold reserves equal to 2% of the outstanding balance of all non-subject financed properties, in addition to standard subject property reserves. This catches experienced investors off guard.",
        },
        {
          id: "mfq7",
          question: "Which of the following best describes a 'house-hack' and why it's valuable to discuss with first-time buyers?",
          options: [
            "Buying a single-family home and renting out rooms — illegal in most jurisdictions",
            "Purchasing a 2–4 unit property owner-occupied, living in one unit, and using tenant rents to offset the mortgage payment",
            "Using rental income from a previous home to qualify for a new primary residence",
            "Converting a garage or basement into an ADU for rental income",
          ],
          correct: 1,
          explanation: "A house-hack is purchasing a 2–4 unit property as an owner-occupant, living in one unit, and collecting rent from the others. It combines low down payment OO financing with investment income — often resulting in a net housing cost far below renting or buying a single-family home. It's one of the most powerful wealth-building conversations an originator can have.",
        },
      ],
    },
  },
];

// ── tm_dscr: DSCR Term Loans ──────────────────────────────────────────────────
export const tm_dscr = [
  {
    id: "dscr_intro",
    title: "What Is DSCR?",
    type: "concept",
    accentColor: "#16a34a",
    content: {
      headline: "The property qualifies itself — no tax returns needed.",
      paragraphs: [
        "DSCR stands for <strong>Debt Service Coverage Ratio</strong>. Instead of verifying the borrower's income, we verify whether the rental income covers the loan payment. No W-2, no paystubs, no tax returns.",
        "For self-employed investors and landlords — which is most of your borrowers — this is a game-changer.",
      ],
      formula: { label: "The Formula", expression: "DSCR = Monthly Rent ÷ PITIA", sub: "PITIA = Principal + Interest + Taxes + Insurance + Association dues" },
      callout: { type: "tip", text: "Practice this 10-second pitch: 'If the rent covers the payment by 25%, you qualify — no tax returns.' That's DSCR in one sentence." },
    },
  },
  {
    id: "dscr_pitia",
    title: "The PITIA Breakdown",
    type: "concept",
    accentColor: "#16a34a",
    content: {
      headline: "Every component of PITIA must be included — missing one kills the deal.",
      breakdown: [
        { letter: "P", word: "Principal", desc: "The portion reducing the loan balance each month" },
        { letter: "I", word: "Interest", desc: "The cost of borrowing — typically the largest component" },
        { letter: "T", word: "Taxes", desc: "Annual property taxes ÷ 12" },
        { letter: "I", word: "Insurance", desc: "Hazard/homeowner's insurance ÷ 12" },
        { letter: "A", word: "Association Dues", desc: "HOA, condo fees, co-op maintenance (if applicable)" },
      ],
      callout: { type: "warning", text: "HOA is the #1 DSCR killer. A $400/mo HOA on a condo can drop a clean 1.25 DSCR to 0.95. Always ask about association dues before quoting DSCR." },
    },
  },
  {
    id: "dscr_thresholds",
    title: "DSCR Thresholds & Programs",
    type: "concept",
    accentColor: "#16a34a",
    content: {
      headline: "Four zones, four programs. Always start at the top.",
      thresholds: [
        { range: "DSCR 1.25+", label: "Sweet spot 🎯", color: "#16a34a", bg: "#f0fdf4", border: "#bbf7d0", desc: "Best rates, full leverage. DSCR 1-4 at up to 85% LTV. This is where you want every deal." },
        { range: "DSCR 1.0 – 1.24", label: "Qualifies", color: "#ca8a04", bg: "#fffbeb", border: "#fde68a", desc: "Standard DSCR 1-4 territory. Qualifies at full LTV. Pricing improves as DSCR rises." },
        { range: "DSCR 0.75 – 0.99", label: "Marginal", color: "#f97316", bg: "#fff7ed", border: "#fed7aa", desc: "700+ credit: DSCR 1-4 still works at lower LTV (80% max). Under 700: No Ratio Term." },
        { range: "DSCR < 0.75", label: "Below floor", color: "#dc2626", bg: "#fef2f2", border: "#fecaca", desc: "No Ratio Term or Credit Flex only. Very limited leverage." },
      ],
      programs: [
        { name: "DSCR 1-4 Unit", badge: "Default", color: "#16a34a", ltv: "85% LTV", min: "DSCR ≥ 1.0 · 660+ credit", desc: "Always try this first. Best rates and highest leverage for standard 1–4 unit rentals." },
        { name: "DSCR Multi (5–9)", badge: "Larger", color: "#2563eb", ltv: "75% LTV", min: "DSCR ≥ 1.0 · 660+ credit · Rent roll + T12", desc: "Requires full rent roll and T12 operating statement. Same DSCR logic, more documentation." },
        { name: "No Ratio Term", badge: "Fallback", color: "#f97316", ltv: "75% LTV", min: "No DSCR floor · 660+ credit", desc: "Removes DSCR requirement. Higher rates, lower leverage. Use when property doesn't cash flow." },
        { name: "Credit Flex", badge: "Last resort", color: "#dc2626", ltv: "65% LTV", min: "Sub-660 credit OK", desc: "Safety net for challenged credit. Highest rates, lowest leverage." },
      ],
      callout: { type: "tip", text: "Always funnel top-down: DSCR 1-4 → No Ratio → Credit Flex. Never lead with a fallback." },
    },
  },
  {
    id: "dscr_rates",
    title: "Rate Adjustments",
    type: "concept",
    accentColor: "#16a34a",
    content: {
      headline: "The base rate is just a starting point — every risk factor stacks.",
      breakdown: [
        { letter: "↑", word: "LTV", desc: "Higher LTV = higher rate. 80% costs more than 70%." },
        { letter: "↑", word: "Credit Score", desc: "700 costs more than 760+. Each bracket moves the needle." },
        { letter: "↑", word: "Interest-Only", desc: "Choosing IO adds +0.25–0.50% to the rate." },
        { letter: "↓", word: "Prepay Penalty", desc: "5-year prepay = lower rate. No prepay = highest rate. Always show both." },
        { letter: "↑", word: "Property Type", desc: "Condos and 2–4 units cost more than SFRs." },
      ],
      callout: { type: "tip", text: "Always show borrowers two rate scenarios: one with short prepay, one with 5-year. That conversation builds trust and closes more deals than a single quote ever will." },
    },
  },
  {
    id: "dscr_calc",
    title: "DSCR Calculator",
    type: "calculator",
    accentColor: "#16a34a",
    content: {
      headline: "Run any rental deal in seconds.",
      intro: "Adjust any input — DSCR, PITIA, and program recommendation update instantly.",
      fields: [
        { key: "rent",   label: "Monthly Rent ($)",  default: 2500,   step: 50 },
        { key: "loan",   label: "Loan Amount ($)",   default: 300000, step: 5000 },
        { key: "rate",   label: "Rate (%)",          default: 7.5,    step: 0.125, min: 3 },
        { key: "years",  label: "Term (yrs)",        default: 30,     step: 1 },
        { key: "taxes",  label: "Taxes/mo ($)",      default: 200,    step: 25 },
        { key: "ins",    label: "Insurance/mo ($)",  default: 100,    step: 25 },
        { key: "hoa",    label: "HOA/mo ($)",        default: 0,      step: 50 },
        { key: "credit", label: "Credit Score",      default: 720,    step: 10, min: 500 },
        { key: "units",  label: "# Units",           default: 1,      step: 1, min: 1 },
      ],
      compute(v) {
        const pi    = calcPI(v.loan, v.rate, v.years);
        const pitia = pi + v.taxes + v.ins + v.hoa;
        const dscr  = pitia > 0 ? v.rent / pitia : 0;
        const color = dscr >= 1.25 ? "#16a34a" : dscr >= 1.0 ? "#ca8a04" : dscr >= 0.75 ? "#f97316" : "#dc2626";
        let prog;
        if (v.units >= 5)                             prog = dscr >= 1.0 && v.credit >= 660 ? "DSCR Multi (5–9) · 75% LTV" : "No Ratio Term · 75% LTV";
        else if (dscr >= 1.0 && v.credit >= 660)     prog = "DSCR 1-4 — Best Rates · 85% LTV";
        else if (dscr >= 0.75 && v.credit >= 700)    prog = "DSCR 1-4 (lower LTV) · 80% max";
        else if (v.credit < 660)                     prog = "Credit Flex · 65% LTV";
        else                                          prog = "No Ratio Term · 75% LTV";
        const tip = dscr >= 1.25 ? "Sweet spot. Best rates available."
          : dscr >= 1.0 ? "Qualifies for DSCR 1-4. Check LTV and credit for rate adjustments."
          : dscr >= 0.75 ? "Marginal. 700+ credit may still qualify for DSCR 1-4 at lower LTV."
          : "Below floor. No Ratio or Credit Flex only. Consider a larger down payment.";
        return { pi, pitia, dscr, color, prog, tip, barPct: Math.min(dscr / 2, 1) * 100 };
      },
      renderResults(r) {
        return {
          highlight: { label: "DSCR", value: r.dscr.toFixed(2), color: r.color, barPct: r.barPct, barLabels: ["0", "1.25 sweet spot", "2.0+"] },
          rows: [
            { label: "Monthly PITIA", value: `$${Math.round(r.pitia)}` },
            { label: "P&I",           value: `$${Math.round(r.pi)}` },
            { label: "Program",       value: r.prog, color: r.color },
          ],
          tip: r.tip,
        };
      },
    },
  },
  {
    id: "dscr_quiz",
    title: "Knowledge Check",
    type: "quiz",
    accentColor: "#16a34a",
    content: {
      passPct: 0.75,
      questions: [
        { id: "dq1", question: "What does DSCR measure?", options: ["Borrower income vs debt", "Rental income vs total monthly loan payment", "Property value vs purchase price", "Equity in the deal vs loan amount"], correct: 1, explanation: "DSCR = Monthly Rent ÷ PITIA. It measures whether the property's rental income covers the full loan payment." },
        { id: "dq2", question: "A condo rents for $2,200/mo. PITIA is $1,960 (including $380 HOA). DSCR?", options: ["1.12", "1.25", "0.97", "1.04"], correct: 0, explanation: "$2,200 ÷ $1,960 = 1.122. The HOA must be included or you'd have PITIA of $1,580 and DSCR of 1.39 — overstate." },
        { id: "dq3", question: "Borrower has DSCR 0.85, credit score 715. Which program?", options: ["Credit Flex", "No Ratio Term", "DSCR 1-4 at lower LTV (max 80%)", "Does not qualify for any program"], correct: 2, explanation: "DSCR 0.75–0.99 + 700+ credit = DSCR 1-4 still works at lower LTV (80% max) and slightly higher rate." },
        { id: "dq4", question: "Why does a longer prepay penalty reduce the interest rate?", options: ["It reduces the lender's risk of default", "The lender is guaranteed a longer income stream", "Longer prepay means lower LTV", "It's a regulatory requirement"], correct: 1, explanation: "A 5-year prepay guarantees the lender income for 5 years. That certainty = lower rate. No prepay = higher rate to compensate for early payoff risk." },
      ],
    },
  },
];

// ── tm_groundup: Ground-Up Construction Loans ─────────────────────────────────
export const tm_groundup = [
  {
    id: "gu_intro",
    title: "What Is a Ground-Up Construction Loan?",
    type: "concept",
    accentColor: "#d97706",
    content: {
      headline: "You're not financing a property — you're financing the creation of one.",
      paragraphs: [
        "A <strong>ground-up construction loan</strong> funds the building of a new structure from scratch — raw land to finished property. The loan is short-term (typically 12–24 months), interest-only, and funds in stages called <strong>draws</strong> as construction milestones are hit.",
        "Unlike a purchase or refinance, the collateral doesn't fully exist yet. The lender is underwriting the borrower's ability to execute the project as much as the property itself. That means builder experience, realistic budgets, and a clear exit strategy are non-negotiable.",
      ],
      callout: {
        type: "tip",
        text: "Practice this 10-second pitch: 'We fund the land and the build in stages — you only pay interest on what's been drawn, and when the CO is issued, you either sell or refi into a permanent loan.' That's ground-up in two sentences.",
      },
    },
  },

  {
    id: "gu_structure",
    title: "How the Loan Is Structured",
    type: "concept",
    accentColor: "#d97706",
    content: {
      headline: "One loan, two buckets: land and construction.",
      breakdown: [
        {
          letter: "L",
          word: "Land Value",
          desc: "The current market value of the lot. LTV is calculated on land + completed value (ARV), not just land alone.",
        },
        {
          letter: "C",
          word: "Construction Budget",
          desc: "Detailed line-item budget covering hard costs (materials, labor) and soft costs (permits, architect, insurance).",
        },
        {
          letter: "D",
          word: "Draw Schedule",
          desc: "Pre-agreed milestones — foundation, framing, rough MEP, drywall, finish — that trigger fund releases after inspection.",
        },
        {
          letter: "R",
          word: "Reserves",
          desc: "Most programs require 6–12 months of interest payments in reserve at close. Protects everyone if the build runs long.",
        },
        {
          letter: "E",
          word: "Exit Strategy",
          desc: "Sale or refinance into permanent financing (DSCR, conv, etc.) upon CO issuance. Underwritten from day one.",
        },
      ],
      callout: {
        type: "warning",
        text: "Soft costs are the #1 budget buster. Permits alone can run $15,000–$50,000+ in some markets. If a borrower's budget has no soft cost line, the project is already underwater. Always review the full itemized budget before quoting.",
      },
    },
  },

  {
    id: "gu_ltv",
    title: "LTC, LTV, and ARV — Know the Difference",
    type: "concept",
    accentColor: "#d97706",
    content: {
      headline: "Three ratios. All three matter. Confuse them and you'll misquote.",
      thresholds: [
        {
          range: "LTC ≤ 85%",
          label: "Loan-to-Cost",
          color: "#16a34a",
          bg: "#f0fdf4",
          border: "#bbf7d0",
          desc: "Loan amount ÷ total project cost (land + construction budget). Controls how much skin-in-the-game borrower has. Most programs max at 80–85% LTC.",
        },
        {
          range: "LTV ≤ 70%",
          label: "Loan-to-Value (ARV)",
          color: "#ca8a04",
          bg: "#fffbeb",
          border: "#fde68a",
          desc: "Loan amount ÷ after-repair (completed) value. The lender's backstop — ensures equity exists at completion. Most programs cap at 65–70% of ARV.",
        },
        {
          range: "Equity ≥ 15–20%",
          label: "Borrower equity",
          color: "#2563eb",
          bg: "#eff6ff",
          border: "#bfdbfe",
          desc: "Borrower must bring real cash to closing — not borrowed funds. Equity is validated at closing with a bank statement or proof of funds.",
        },
        {
          range: "Both must pass",
          label: "The binding constraint",
          color: "#dc2626",
          bg: "#fef2f2",
          border: "#fecaca",
          desc: "The loan amount is the LOWER of the LTC max and the ARV max. Run both and take the smaller number — every time.",
        },
      ],
      callout: {
        type: "tip",
        text: "Quick check: 'What's the all-in cost and what will it be worth finished?' Run LTC. Run LTV on ARV. Take the lower number. If that number doesn't work for the borrower, they need more equity.",
      },
    },
  },

  {
    id: "gu_programs",
    title: "Programs & Eligibility",
    type: "concept",
    accentColor: "#d97706",
    content: {
      headline: "Match the borrower's experience and project scope to the right program first.",
      programs: [
        {
          name: "Standard Ground-Up (1–4 unit)",
          badge: "Default",
          color: "#16a34a",
          ltv: "85% LTC / 70% ARV",
          min: "660+ credit · 1+ completed project · Full budget + plans",
          desc: "SFR, duplex, triplex, quad. Requires at least one prior new-construction or substantial rehab completed. Most common scenario for experienced investors.",
        },
        {
          name: "Spec Build / Presale",
          badge: "Stronger profile",
          color: "#2563eb",
          ltv: "80% LTC / 65% ARV",
          min: "680+ credit · 3+ projects · Market comp analysis required",
          desc: "Building to sell, not hold. Underwriter wants evidence of demand — active listings, absorption rate, comp sales within 1 mile. Stronger borrower profile required.",
        },
        {
          name: "Multi-Unit Ground-Up (5+ units)",
          badge: "Complex",
          color: "#7c3aed",
          ltv: "75% LTC / 65% ARV",
          min: "700+ credit · 5+ projects · Full pro forma + entitlements",
          desc: "Requires full entitlements in hand, stabilized pro forma, and demonstrated track record at scale. Expect longer underwriting and third-party feasibility review.",
        },
        {
          name: "First-Time Builder Bridge",
          badge: "Fallback",
          color: "#f97316",
          ltv: "70% LTC / 60% ARV",
          min: "680+ credit · GC partnership required · Lower leverage",
          desc: "For borrowers without a completed ground-up project. Only viable if paired with an experienced licensed GC as a partner. Higher rates, lower leverage, tighter draw controls.",
        },
      ],
      callout: {
        type: "tip",
        text: "Always ask: 'How many ground-up projects have you completed — not rehabbed, built from dirt?' That single question determines which program is available before you run a single number.",
      },
    },
  },

  {
    id: "gu_draws",
    title: "The Draw Process",
    type: "concept",
    accentColor: "#d97706",
    content: {
      headline: "Draws are not automatic — they're earned milestone by milestone.",
      paragraphs: [
        "Once the loan closes, funds are held in a <strong>construction holdback</strong>. The borrower requests draws as each milestone is completed. An inspector visits the site, confirms the work, and the lender releases funds — typically within 5–10 business days of an approved inspection.",
        "Interest accrues only on the <strong>drawn balance</strong>, not the full loan amount. A $600K loan with $150K drawn = interest on $150K only. This is a key selling point for borrowers worried about carry costs.",
      ],
      breakdown: [
        { letter: "1", word: "Foundation & site work", desc: "Typically 10–15% of budget. Must be fully complete and inspected before release." },
        { letter: "2", word: "Framing", desc: "20–25% of budget. Includes exterior sheathing, roof deck, windows rough-in." },
        { letter: "3", word: "Rough MEP", desc: "Mechanical, electrical, plumbing rough-in. Inspections required per jurisdiction." },
        { letter: "4", word: "Drywall + insulation", desc: "Interior work begins. Usually 15–20% of budget drawn at this stage." },
        { letter: "5", word: "Finish + punch list", desc: "Fixtures, flooring, paint, landscaping. Final draw held until CO is issued." },
      ],
      callout: {
        type: "warning",
        text: "The final draw (typically 10%) is held until the Certificate of Occupancy is in hand. If a borrower is planning to use that final draw to fund their move to the next project, they need to know this upfront — not at the finish line.",
      },
    },
  },

  {
    id: "gu_rates",
    title: "Rate & Pricing Adjustments",
    type: "concept",
    accentColor: "#d97706",
    content: {
      headline: "Ground-up rates are higher than DSCR — the risk profile is different.",
      paragraphs: [
        "Construction loans carry more risk than stabilized investment properties: no income stream yet, collateral doesn't fully exist, execution risk is real. Expect rates <strong>1.5–3% above</strong> equivalent DSCR product, with larger point fees.",
      ],
      breakdown: [
        { letter: "↑", word: "Borrower experience", desc: "First-time builder adds 0.50–1.00% and reduces available LTC. This is the biggest pricing lever." },
        { letter: "↑", word: "Project complexity", desc: "Custom design, hillside lot, unusual materials = harder to re-sell if deal fails. Higher rate." },
        { letter: "↑", word: "LTC / LTV", desc: "Higher leverage = higher rate. 85% LTC costs meaningfully more than 70% LTC." },
        { letter: "↑", word: "Market / location", desc: "Rural or low-demand markets increase absorption risk. Underwriters price it in." },
        { letter: "↓", word: "Presale contract", desc: "A signed purchase contract from an end buyer at closing can reduce rate and improve LTC. Strongest risk mitigant available." },
        { letter: "↓", word: "Larger reserves", desc: "Posting 12 months of reserves vs. 6 months demonstrates strength and may improve pricing." },
      ],
      callout: {
        type: "tip",
        text: "Always show the borrower the cost of the full carry: rate × drawn balance × expected months to CO. A 11% rate on $500K drawn for 14 months is ~$64K in interest. That number belongs in their pro forma from day one.",
      },
    },
  },

  {
    id: "gu_calc",
    title: "Ground-Up Project Calculator",
    type: "calculator",
    accentColor: "#d97706",
    content: {
      headline: "Stress-test any ground-up deal in seconds.",
      intro: "Adjust inputs — LTC, ARV LTV, and feasibility flag update instantly.",
      fields: [
        { key: "land",    label: "Land Value ($)",          default: 150000, step: 5000 },
        { key: "budget",  label: "Construction Budget ($)",  default: 400000, step: 5000 },
        { key: "arv",     label: "After-Repair Value ($)",  default: 750000, step: 10000 },
        { key: "rate",    label: "Rate (%)",                default: 11.5,   step: 0.25, min: 7 },
        { key: "months",  label: "Build Time (months)",     default: 14,     step: 1, min: 6 },
        { key: "credit",  label: "Credit Score",            default: 700,    step: 10, min: 620 },
        { key: "exp",     label: "Prior Projects Completed",default: 2,      step: 1, min: 0 },
      ],
      compute(v) {
        const totalCost  = v.land + v.budget;
        const maxByLTC   = totalCost * 0.85;
        const maxByARV   = v.arv * 0.70;
        const loanAmt    = Math.min(maxByLTC, maxByARV);
        const ltc        = loanAmt / totalCost;
        const ltv        = loanAmt / v.arv;
        const avgDraw    = loanAmt * 0.55; // ~55% average outstanding
        const totalInt   = avgDraw * (v.rate / 100) * (v.months / 12);
        const profitEst  = v.arv - totalCost - totalInt;
        const roi        = profitEst / (totalCost - loanAmt);
        const feasible   = ltc <= 0.85 && ltv <= 0.70 && v.credit >= 660 && v.exp >= 1 && profitEst > 0;
        const color      = feasible ? "#16a34a" : ltc > 0.85 || ltv > 0.70 ? "#dc2626" : "#ca8a04";
        const prog       = v.exp === 0 ? "First-Time Builder Bridge · 70% LTC / 60% ARV" :
                           v.exp < 3   ? "Standard Ground-Up · 85% LTC / 70% ARV" :
                                         "Spec Build eligible · 80% LTC / 65% ARV";
        const tip        = !feasible && ltc > 0.85 ? "LTC exceeds 85%. Borrower needs more equity or a lower budget." :
                           !feasible && ltv > 0.70 ? "ARV LTV exceeds 70%. Increase ARV estimate or reduce loan." :
                           !feasible && v.exp < 1  ? "No completed projects. Must use First-Time Builder program with licensed GC." :
                           !feasible && profitEst <= 0 ? "Deal doesn't pencil — projected profit is negative after interest carry." :
                           "Deal structurally feasible. Confirm budget line items and exit strategy.";
        return { loanAmt, ltc, ltv, totalInt, profitEst, roi, color, prog, tip, feasible };
      },
      renderResults(r) {
        return {
          highlight: {
            label: "Max Loan",
            value: `$${Math.round(r.loanAmt / 1000)}K`,
            color: r.color,
          },
          rows: [
            { label: "LTC",              value: `${(r.ltc * 100).toFixed(1)}%`,        color: r.ltc > 0.85 ? "#dc2626" : "#16a34a" },
            { label: "ARV LTV",          value: `${(r.ltv * 100).toFixed(1)}%`,        color: r.ltv > 0.70 ? "#dc2626" : "#16a34a" },
            { label: "Est. interest carry", value: `$${Math.round(r.totalInt).toLocaleString()}` },
            { label: "Est. gross profit",   value: `$${Math.round(r.profitEst).toLocaleString()}`, color: r.profitEst < 0 ? "#dc2626" : "#16a34a" },
            { label: "Program",          value: r.prog, color: r.color },
          ],
          tip: r.tip,
        };
      },
    },
  },

  {
    id: "gu_quiz",
    title: "Knowledge Check",
    type: "quiz",
    accentColor: "#d97706",
    content: {
      passPct: 0.75,
      questions: [
        {
          id: "gq1",
          question: "A borrower has land worth $200K and a $500K construction budget. The ARV is $850K. What is the maximum loan using an 85% LTC / 70% ARV program?",
          options: ["$595,000", "$595,000 — but ARV cap overrides at $595,000", "$490,000 — ARV cap is the binding constraint", "$595,000 — LTC is always the binding constraint"],
          correct: 2,
          explanation: "Total cost = $700K. 85% LTC = $595K. 70% of $850K ARV = $595K. Both land on the same number here — but in most deals they won't. Always calculate both and take the lower one.",
        },
        {
          id: "gq2",
          question: "Why does interest accrue only on the drawn balance, not the full loan amount?",
          options: [
            "It's a regulatory requirement for construction loans",
            "Funds not yet drawn sit in a holdback — the borrower hasn't received them yet",
            "The lender waives interest on the undrawn portion as an incentive",
            "Construction loans use a flat fee instead of accruing interest",
          ],
          correct: 1,
          explanation: "The full loan amount is committed but held in a construction holdback. Until a draw is requested and released after inspection, that money hasn't left the lender — so no interest accrues on it.",
        },
        {
          id: "gq3",
          question: "A borrower has no prior ground-up projects completed. Which is the correct program path?",
          options: [
            "Standard Ground-Up at full leverage — experience doesn't matter",
            "Spec Build program with presale contract",
            "First-Time Builder Bridge — lower LTC, licensed GC required",
            "They do not qualify for any construction program",
          ],
          correct: 2,
          explanation: "Zero completed projects means Standard Ground-Up is unavailable. The First-Time Builder Bridge is the only path — lower leverage (70% LTC / 60% ARV), requires a licensed, experienced GC as a project partner.",
        },
        {
          id: "gq4",
          question: "Why is the final draw (typically ~10% of budget) held until the Certificate of Occupancy is issued?",
          options: [
            "It's reserved for the borrower's real estate agent commission",
            "It ensures the borrower completes the project rather than abandoning it at 90%",
            "It pays the lender's inspection fee at completion",
            "Lenders are required by law to withhold the final 10%",
          ],
          correct: 1,
          explanation: "Holding 10% back until the CO is issued is the lender's primary completion incentive. Without it, a borrower who runs out of motivation (or money) at 90% could walk away, leaving the lender with an incomplete, unmarketable asset.",
        },
        {
          id: "gq5",
          question: "A borrower asks why their ground-up rate is 3% higher than their neighbor's DSCR rate. What's the best explanation?",
          options: [
            "Construction loans have a regulatory surcharge",
            "The lender charges more because it's more paperwork",
            "A completed rental generates income and is easier to sell if needed — a half-built house is neither",
            "Construction loans are riskier because they're shorter term",
          ],
          correct: 2,
          explanation: "A stabilized rental produces rental income, has a known value, and can be sold at any time. A construction project in mid-build has no income, uncertain value, and is extremely difficult to liquidate. That illiquidity and execution risk is priced into the rate.",
        },
      ],
    },
  },
];
// ════════════════════════════════════════════════════════════════════════════
// SALES & CLOSING
// ════════════════════════════════════════════════════════════════════════════

// ── tm_discovery: The Discovery Call ─────────────────────────────────────────
export const tm_discovery = [
  {
    id: "dc_intro",
    title: "The 20-Minute Discovery Call",
    type: "concept",
    content: {
      text: `
Goal: qualify, structure, and move to next step FAST.
      `,
    },
  },
  {
    id: "dc_questions",
    title: "Key Questions",
    type: "concept",
    content: {
      text: `
Ask:

• What’s the deal?
• Purchase price?
• Rehab budget?
• Exit strategy?
• Timeline?

You’re gathering underwriting data.
      `,
    },
  },
  {
    id: "dc_control",
    title: "Control the Call",
    type: "concept",
    content: {
      text: `
Don’t let borrowers ramble.

Guide the conversation like an underwriter.
      `,
    },
  },
];

// ════════════════════════════════════════════════════════════════════════════
// COMPANY & TOOLS
// ════════════════════════════════════════════════════════════════════════════

// ── tm_company: Propertunity 101 ──────────────────────────────────────────────
export const tm_company = [
  {
    id: "co_intro",
    title: "Who We Are",
    type: "concept",
    accentColor: "#2563eb",
    content: {
      headline: "Propertunity Capital: the lender that says yes when banks say no.",
      paragraphs: [
        "Propertunity Capital is a real-estate lending platform connecting investors with bridge loans, DSCR term loans, ground up construction, and multifamily financing across Florida and beyond.",
        "Our partnership with <strong>Lem Lux Realty</strong> puts us inside the deal flow — brokers, flippers, and landlords all move through the same ecosystem.",
      ],
      callout: { type: "tip", text: "One-sentence company pitch: 'We're the lender that closes investment deals in 7 days — no income docs, no bank process.' Memorize it." },
    },
  },
  {
    id: "co_role",
    title: "Your Role",
    type: "concept",
    accentColor: "#2563eb",
    content: {
      headline: "Qualify. Educate. Hand off a clean file.",
      breakdown: [
        { letter: "1", word: "Qualify", desc: "Determine if the deal and borrower meet our basic criteria before spending anyone's time" },
        { letter: "2", word: "Educate", desc: "Match the borrower to the right product and explain why it fits their goals" },
        { letter: "3", word: "Hand Off", desc: "Submit a complete file to underwriting — all four data points ready" },
      ],
      callout: { type: "warning", text: "Incomplete files are the #1 cause of delays and the #1 reason deals fall through. Never submit without ARV, purchase+rehab, track record, and exit strategy." },
    },
  },
  {
    id: "co_culture",
    title: "Culture & Expectations",
    type: "concept",
    accentColor: "#2563eb",
    content: {
      headline: "Fast, clear, professional — every time.",
      list: [
        "<strong>24-hour follow-up</strong> — no exceptions. Speed of response signals professionalism",
        "<strong>CRM updated after every interaction</strong> — if it's not logged, it didn't happen",
        "No deal is too small to treat with full professionalism",
        "Escalate compliance questions immediately — never guess",
        "Under-promise on timelines, over-deliver on execution",
      ],
      callout: { type: "tip", text: "The reps who hit quota aren't always the best salespeople — they're the most consistent. Habits beat talent every time." },
    },
  },
  {
    id: "co_quiz",
    title: "Knowledge Check",
    type: "quiz",
    accentColor: "#2563eb",
    content: {
      passPct: 0.75,
      questions: [
        { id: "coq1", question: "What is the sales rep's three-part role?", options: ["Pitch, close, fund", "Qualify, educate, hand off to underwriting", "Source, underwrite, close", "Call, follow up, collect"], correct: 1, explanation: "Qualify the borrower, educate them on the right product, and hand off a complete file to underwriting." },
        { id: "coq2", question: "What is the follow-up standard?", options: ["48 hours", "72 hours", "Within 24 hours", "By end of week"], correct: 2, explanation: "24 hours — no exceptions. Speed of follow-up is directly correlated with deal conversion." },
        { id: "coq3", question: "What are the four things needed before submitting to underwriting?", options: ["Credit, income, LTV, appraisal", "ARV, purchase+rehab, track record, exit strategy", "Deal type, property type, loan term, rate", "Name, address, loan amount, down payment"], correct: 1, explanation: "ARV, purchase+rehab budget, borrower track record, and a defined exit strategy." },
        { id: "coq4", question: "Who is Propertunity's primary referral partner?", options: ["Chase Bank", "Marcus & Millichap", "Lem Lux Realty", "CBRE"], correct: 2, explanation: "Lem Lux Realty is our primary partner, putting us at the center of deal flow." },
      ],
    },
  },
];

// ── tm_crm: CRM Mastery ───────────────────────────────────────────────────────
export const tm_crm = [
  {
    id: "crm_stages",
    title: "Pipeline Stages",
    type: "concept",
    accentColor: "#ca8a04",
    content: {
      headline: "Five stages. A lead should never stall more than 5 business days without a logged action.",
      breakdown: [
        { letter: "1", word: "New Lead",        desc: "Just entered the system. Contact within 24 hours." },
        { letter: "2", word: "Contacted",        desc: "First outreach made. Waiting on discovery call." },
        { letter: "3", word: "Qualified",        desc: "Discovery call done. Deal fits our criteria." },
        { letter: "4", word: "Term Sheet Sent",  desc: "Formal offer out. Follow up every 2 days until decision." },
        { letter: "5", word: "Funded",           desc: "Deal closed. Trigger referral ask and portfolio follow-up." },
      ],
      callout: { type: "warning", text: "A lead stalled in the same stage for 5+ business days with no activity logged is a red flag managers review weekly." },
    },
  },
  {
    id: "crm_logging",
    title: "Activity Logging",
    type: "concept",
    accentColor: "#ca8a04",
    content: {
      headline: "Log every call, email, and text within 2 hours. Always.",
      list: [
        "Record the <strong>outcome</strong> — not just that the interaction happened",
        "Always log the <strong>next step</strong> with a specific date and action",
        "Managers review logs weekly — clean logs reflect professional habits",
        "If it's not in the CRM, it didn't happen",
        "Use call notes to capture borrower priorities — they inform every future pitch",
      ],
      callout: { type: "tip", text: "Reps who log same-day have 30% higher conversion. The notes become pitch prep for the next call." },
    },
  },
  {
    id: "crm_cadence",
    title: "Follow-Up Cadence",
    type: "concept",
    accentColor: "#ca8a04",
    content: {
      headline: "Persistence with professionalism beats a perfect pitch.",
      table: {
        headers: ["Day", "Action", "Goal"],
        rows: [
          ["Day 1",  "Phone call",          "Warm intro, needs assessment"],
          ["Day 3",  "Email",               "Recap value, send resources"],
          ["Day 7",  "Call + text",         "Check for questions, create urgency"],
          ["Day 14", "Breakup email",       "Re-engage or close the loop"],
          ["Day 30+","Monthly newsletter",  "Stay top of mind for future deals"],
        ],
      },
      callout: { type: "tip", text: "The Day 14 breakup email gets more responses than almost any other touchpoint. Try: 'I don't want to keep bothering you — if timing's off, no worries. Just let me know.' It works." },
    },
  },
  {
    id: "crm_forecast",
    title: "Forecasting & Weekly Review",
    type: "concept",
    accentColor: "#ca8a04",
    content: {
      headline: "Review every Monday. Your forecast is only as good as your data.",
      list: [
        "Identify deals that could close <strong>this month</strong> and assign one specific action to each",
        "Flag stalled deals: push harder or disqualify and move on",
        "Update probabilities based on actual deal status — not wishful thinking",
        "Your weekly forecast is reviewed by your manager — make it accurate",
        "Garbage in, garbage out. A padded pipeline helps no one",
      ],
      callout: { type: "warning", text: "Inflating your forecast to look good is a career-shortener. An accurate pipeline — even a small one — earns more trust than a padded one." },
    },
  },
  {
    id: "crm_quiz",
    title: "Knowledge Check",
    type: "quiz",
    accentColor: "#ca8a04",
    content: {
      passPct: 0.75,
      questions: [
        { id: "crmq1", question: "How long can a lead sit in one stage without a logged activity?", options: ["10 business days", "7 calendar days", "5 business days", "30 days"], correct: 2, explanation: "5 business days is the maximum. Beyond that, the lead is flagged in manager reviews." },
        { id: "crmq2", question: "What must every logged activity include beyond the interaction itself?", options: ["Deal's estimated close date", "Outcome + next step with a specific date", "Borrower's credit score", "Loan amount and property address"], correct: 1, explanation: "Every log needs the outcome (what happened) and the next step (what happens next, with a date)." },
        { id: "crmq3", question: "What is the purpose of the Day 14 breakup email?", options: ["Remove the lead from CRM", "Re-engage silent leads or close the loop", "Escalate to a manager", "Send a final term sheet"], correct: 1, explanation: "The breakup email often re-engages leads who've gone quiet. It respects their time and frequently prompts a response." },
        { id: "crmq4", question: "When should you log an activity after a call?", options: ["End of the day", "Within 2 hours", "By end of week", "Before your next call"], correct: 1, explanation: "Within 2 hours while details are fresh. Same-day logging correlates with 30% higher conversion." },
      ],
    },
  },
];

// ── tm_compliance: Compliance & Fair Lending ──────────────────────────────────
export const tm_compliance = [
  {
    id: "cmp_respa",
    title: "RESPA Overview",
    type: "concept",
    accentColor: "#dc2626",
    content: {
      headline: "RESPA governs fees and referrals. Never accept anything of value for a referral.",
      paragraphs: [
        "The <strong>Real Estate Settlement Procedures Act (RESPA)</strong> requires transparent disclosure of all settlement costs and prohibits kickbacks for referrals.",
        "Any affiliated business arrangement — like our Lem Lux Realty partnership — must be disclosed in writing to the borrower before closing.",
      ],
      list: [
        "Never accept or give cash, gifts, or anything of value in exchange for a referral",
        "All affiliated business arrangements must be disclosed in writing at or before application",
        "RESPA violations carry civil and criminal penalties",
        "Escalate any referral arrangement questions to compliance immediately",
      ],
      callout: { type: "warning", text: "If a referral partner suggests any kind of 'thank you' arrangement, stop the conversation immediately and report it to your manager. RESPA violations are not a gray area." },
    },
  },
  {
    id: "cmp_fair",
    title: "Fair Lending Laws",
    type: "concept",
    accentColor: "#dc2626",
    content: {
      headline: "Every borrower is evaluated on identical criteria. No exceptions.",
      paragraphs: [
        "The <strong>Equal Credit Opportunity Act (ECOA)</strong> and <strong>Fair Housing Act</strong> prohibit discrimination based on race, color, religion, national origin, sex, familial status, or disability.",
      ],
      list: [
        "Never quote different rates or terms to comparable borrowers based on protected characteristics",
        "Steering borrowers to certain products based on protected class is illegal",
        "Document every credit decision with objective, deal-based criteria only",
        "Disparate impact — unintentional discrimination through neutral-looking policies — is also actionable",
      ],
      callout: { type: "warning", text: "You don't have to intend to discriminate to be liable. If a pattern of decisions disproportionately affects a protected class, that is a fair lending violation." },
    },
  },
  {
    id: "cmp_privacy",
    title: "Privacy & Data Handling",
    type: "concept",
    accentColor: "#dc2626",
    content: {
      headline: "Borrower data is confidential. Treat it that way.",
      list: [
        "Never share loan details, income, or credit info outside of the deal team",
        "Never discuss deals in public spaces or over unsecured channels",
        "All borrower data must be stored only in approved platforms (CRM + secure file sharing)",
        "If you suspect a data breach, report it to your manager immediately",
      ],
      callout: { type: "warning", text: "Mentioning a borrower's deal by name — even casually — to someone outside the deal team is a privacy violation. This includes conversations with other reps." },
    },
  },
  {
    id: "cmp_dos",
    title: "Do's and Don'ts",
    type: "concept",
    accentColor: "#dc2626",
    content: {
      headline: "The non-negotiables.",
      examples: [
        { name: "DO ✅", emoji: "✅", color: "#16a34a", bg: "#f0fdf4", outcome: "Document everything in the CRM. Escalate compliance questions immediately. Get everything in writing. Know your RESPA disclosure obligations before closing." },
        { name: "DON'T ❌", emoji: "❌", color: "#dc2626", bg: "#fef2f2", outcome: "Make promises about rates or approvals before underwriting. Accept cash, gifts, or off-platform payments. Quote different terms to comparable borrowers. Discuss deals outside secure channels." },
      ],
      callout: { type: "warning", text: "When in doubt, don't. Escalate immediately. A 5-minute conversation with your manager today is better than a compliance issue next month." },
    },
  },
  {
    id: "cmp_quiz",
    title: "Required Knowledge Check",
    type: "quiz",
    accentColor: "#dc2626",
    content: {
      passPct: 1.0,
      intro: "This is a required module. You must answer all 4 questions correctly to pass.",
      questions: [
        { id: "cmpq1", question: "What does RESPA prohibit?", options: ["Charging origination fees", "Kickbacks — giving or receiving anything of value for referrals", "Working with affiliated businesses", "Disclosing settlement costs"], correct: 1, explanation: "RESPA prohibits kickbacks of any kind in exchange for referrals." },
        { id: "cmpq2", question: "Which of the following is a Fair Housing Act violation?", options: ["Requiring higher rates for lower credit scores", "Quoting different rates based on national origin", "Declining a loan based on DSCR", "Requiring income documentation"], correct: 1, explanation: "Charging different rates or terms based on protected characteristics (race, national origin, etc.) is prohibited." },
        { id: "cmpq3", question: "A referral partner offers to buy you dinner as a thank-you. You should:", options: ["Accept — it's just a dinner", "Accept only if under $50", "Decline and report the conversation to your manager", "Accept and disclose it in the CRM"], correct: 2, explanation: "Any gift or thing of value in a referral context is a RESPA violation. Decline and escalate." },
        { id: "cmpq4", question: "You suspect a colleague is quoting higher rates to certain borrowers based on national origin. You should:", options: ["Wait and monitor for a few more weeks", "Confront the colleague directly", "Report it to your manager or compliance team immediately", "Do nothing — it's not your responsibility"], correct: 2, explanation: "Fair lending violations must be reported immediately. You have an obligation to escalate." },
      ],
    },
  },
];

// ─── Master map ───────────────────────────────────────────────────────────────
export const LESSON_DATA_MAP = {
  tm_bridge:      tm_bridge,
  tm_groundup:    tm_groundup,
  tm_multifamily: tm_multifamily,
  tm_dscr:        tm_dscr,
  tm_discovery:   tm_discovery,
  tm_company:     tm_company,
  tm_crm:         tm_crm,
  tm_compliance:  tm_compliance,
};